import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { fetchAppConfig } from "@/lib/appConfig";
import { useNavigate } from "react-router-dom";
import {
  ChevronRight, Play, BookOpen, Crown, Medal, Trophy, TestTube2,
  Share2, MessageSquare, Newspaper, ExternalLink, ShoppingBag,
  Search, Gift, Boxes, Microscope, Linkedin
} from "lucide-react";
import { toTitleCase } from "@/lib/utils";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const TIER_THRESHOLDS = { Silver: 0, Gold: 5000, Platinum: 15000 };
const TIER_ORDER = ["Silver", "Gold", "Platinum"];
// Trophy icon across all tiers — color differentiates slab
const TIER_ICON = { Silver: Trophy, Gold: Trophy, Platinum: Trophy };
// Solid metallic gradient backgrounds — mirrors the Digital Card on /app/points
// so the same surface appears on Home tier tracker, Profile header and the card.
const TIER_BG = {
  Silver:   "from-[#B6C2D0] via-[#8C99A8] to-[#3A4555]",
  Gold:     "from-[#FBE38B] via-[#D4A93A] to-[#8C6618]",
  Platinum: "from-[#2B50B0] via-[#173075] to-[#0A1736]",
};

export default function CustomerDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(null);
  const [banners, setBanners] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [products, setProducts] = useState([]);
  const [newLaunchAssets, setNewLaunchAssets] = useState({});
  const [training, setTraining] = useState([]);
  const [news, setNews] = useState([]);
  const [activeBanner, setActiveBanner] = useState(0);
  const [search, setSearch] = useState("");
  const [suggest, setSuggest] = useState({ instruments: [], reagents: [], segments: [] });
  const [showSuggest, setShowSuggest] = useState(false);
  const [about, setAbout] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [balRes, bannerRes, featRes, prodRes, trainRes, newsRes, assetRes, aboutRes] = await Promise.all([
        API.get("/points/balance"),
        API.get("/banners/active").catch(() => ({ data: [] })),
        API.get("/products?is_featured=true&limit=2").catch(() => ({ data: { products: [] } })),
        API.get("/products?limit=12").catch(() => ({ data: { products: [] } })),
        API.get("/content/training").catch(() => ({ data: [] })),
        API.get("/content/news-events").catch(() => ({ data: [] })),
        API.get("/assets?category=new_launch").catch(() => ({ data: [] })),
        API.get("/about-content").catch(() => ({ data: null })),
      ]);
      setBalance(balRes.data);
      setBanners(bannerRes.data || []);
      setFeatured(featRes.data.products || []);
      setProducts(prodRes.data.products || []);
      setTraining(trainRes.data || []);
      setNews(newsRes.data || []);
      setAbout(aboutRes.data || null);
      const nl = {};
      (assetRes.data || []).forEach(a => { nl[a.key] = a; });
      setNewLaunchAssets(nl);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { if (banners.length > 1) { const t = setInterval(() => setActiveBanner(p => (p + 1) % banners.length), 4000); return () => clearInterval(t); } }, [banners]);

  // Predictive search — debounced suggest call, triggered from the input handler
  // (kept out of an effect so state updates stay inside the event handler).
  const suggestTimer = useRef(null);
  const handleSearchChange = (value) => {
    setSearch(value);
    setShowSuggest(true);
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    const q = value.trim();
    if (q.length < 2) {
      setSuggest({ instruments: [], reagents: [], segments: [] });
      return;
    }
    suggestTimer.current = setTimeout(async () => {
      try {
        const r = await API.get(`/search/suggest?q=${encodeURIComponent(q)}&limit=8`);
        setSuggest(r.data || { instruments: [], reagents: [], segments: [] });
      } catch { /* ignore */ }
    }, 200);
  };
  useEffect(() => () => { if (suggestTimer.current) clearTimeout(suggestTimer.current); }, []);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(pos => {
        API.post("/auth/capture-location", { lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {});
      }, () => {});
    }
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = search.trim();
    if (q) navigate(`/app/products?search=${encodeURIComponent(q)}`);
  };

  const tier = user?.tier || "Silver";
  const [tierDesign, setTierDesign] = useState({});
  const [appTiers, setAppTiers] = useState([]);
  useEffect(() => { fetchAppConfig().then(cfg => { setTierDesign(cfg.tier_design || {}); setAppTiers(cfg.tiers || []); }); }, []);

  // Point 31: tapping any displayed banner opens the TransAsia Offers page
  // inside the in-app web viewer (proxied for transasia.co.in).
  const openBanner = () => {
    navigate(`/app/web?url=${encodeURIComponent("https://transasia.co.in/offers")}&title=${encodeURIComponent("Offers")}`);
  };

  const totalEarned = balance?.total_points_earned || user?.total_points_earned || 0;

  // Point 20: derive tier name / thresholds / card design from admin config so
  // renamed tiers and updated designs reflect in the app immediately. Falls back
  // to the built-in defaults when config isn't available.
  const sortedTiers = [...appTiers].sort((a, b) => (a.min_points || 0) - (b.min_points || 0));
  let curTierCfg = null, nextTierCfg = null;
  if (sortedTiers.length) {
    let idx = sortedTiers.findIndex(t => (t.name || "").toLowerCase() === (tier || "").toLowerCase());
    if (idx < 0) { idx = 0; for (let i = 0; i < sortedTiers.length; i++) { if (totalEarned >= (sortedTiers[i].min_points || 0)) idx = i; } }
    curTierCfg = sortedTiers[idx];
    nextTierCfg = sortedTiers[idx + 1] || null;
  }

  const tierName = curTierCfg?.name || tier;
  const design = curTierCfg
    ? { card_image_url: curTierCfg.card_image_url, color_text: curTierCfg.color_text, color_accent: curTierCfg.color_accent, color_primary: curTierCfg.color_primary }
    : (tierDesign[tier] || {});
  const customImg = design.card_image_url ? (design.card_image_url.startsWith("http") ? design.card_image_url : `${BACKEND}${design.card_image_url}`) : "";
  const customText = design.color_text || "";
  const customAccent = design.color_accent || "";

  const hcIndex = TIER_ORDER.indexOf(tier);
  const nextTier = curTierCfg ? (nextTierCfg ? nextTierCfg.name : null) : (hcIndex < TIER_ORDER.length - 1 ? TIER_ORDER[hcIndex + 1] : null);
  const currentThreshold = curTierCfg ? (curTierCfg.min_points || 0) : (TIER_THRESHOLDS[tier] || 0);
  const nextThreshold = nextTierCfg ? (nextTierCfg.min_points || 0) : (nextTier ? (TIER_THRESHOLDS[nextTier] || currentThreshold) : currentThreshold);
  const pointsToNext = nextThreshold > currentThreshold ? Math.max(0, nextThreshold - totalEarned) : 0;
  const progressPercent = nextThreshold > currentThreshold ? Math.min(100, ((totalEarned - currentThreshold) / (nextThreshold - currentThreshold)) * 100) : 100;
  const TierIcon = TIER_ICON[tierName] || TIER_ICON[tier] || Medal;
  const balPts = balance?.balance_points || user?.balance_points || 0;

  // Platinum (or a custom card image) uses light text for legibility
  const isDarkTier = tierName === "Platinum" || !!customImg;
  const tierText = isDarkTier ? "text-white" : "text-[#0A2540]";
  const tierTextDim = isDarkTier ? "text-white/80" : "text-[#0A2540]/75";
  const tierTextSoft = isDarkTier ? "text-white/65" : "text-[#0A2540]/65";
  const tierShadow = isDarkTier ? "drop-shadow-[0_1px_2px_rgba(0,0,0,0.35)]" : "drop-shadow-[0_1px_1px_rgba(255,255,255,0.35)]";
  const textStyle = customText ? { color: customText } : undefined;

  // Quick actions: Products, Refer & Earn
  const quickActions = [
    { icon: TestTube2, label: "Products", path: "/app/products", color: "bg-[#E0F4F5]", textColor: "text-[#008A99]" },
    { icon: Share2, label: "Refer & Earn", path: "/app/referral", color: "bg-[#E0F4F5]", textColor: "text-[#008A99]" },
  ];

  const featuredFallback = featured.length >= 2 ? featured : featured.concat(products.slice(0, 2 - featured.length));

  return (
    <div className="space-y-5 -mt-2" data-testid="customer-dashboard">
      {/* Search Bar with predictive suggestions */}
      <div className="relative" data-testid="home-search">
        <form onSubmit={handleSearchSubmit}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            onFocus={() => setShowSuggest(true)}
            onBlur={() => setTimeout(() => setShowSuggest(false), 180)}
            placeholder="Search segments, instruments, reagents…"
            className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-2xl text-sm focus:outline-none focus:border-[#008A99] focus:ring-2 focus:ring-[#008A99]/10 transition-all"
            data-testid="home-search-input"
          />
        </form>
        {showSuggest && search.trim().length >= 2 && (suggest.instruments.length + suggest.reagents.length + suggest.segments.length > 0) && (
          <div
            className="absolute z-30 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-y-auto divide-y divide-gray-100"
            data-testid="search-suggestions"
          >
            {suggest.segments.length > 0 && (
              <div className="p-2">
                {suggest.segments.map(s => (
                  <button key={s} onMouseDown={e => e.preventDefault()}
                    onClick={() => { navigate(`/app/products?segment=${encodeURIComponent(s)}`); setShowSuggest(false); }}
                    className="w-full flex items-center justify-between gap-2 px-2 py-2 hover:bg-[#E0F4F5] rounded-md text-xs"
                    data-testid={`suggest-segment-${s}`}
                  >
                    <span className="font-semibold text-gray-800 truncate flex-1 text-left">{toTitleCase(s)}</span>
                    <span className="flex-shrink-0 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">Segment</span>
                  </button>
                ))}
              </div>
            )}
            {suggest.instruments.length > 0 && (
              <div className="p-2">
                {suggest.instruments.map(i => (
                  <button key={i.instrument_code} onMouseDown={e => e.preventDefault()}
                    onClick={() => { navigate(`/app/products?instrument=${encodeURIComponent(i.instrument_code)}`); setShowSuggest(false); }}
                    className="w-full flex items-center justify-between gap-2 px-2 py-2 hover:bg-[#E0F4F5] rounded-md text-xs"
                    data-testid={`suggest-instrument-${i.instrument_code}`}
                  >
                    <div className="min-w-0 flex-1 text-left">
                      <p className="font-semibold text-gray-800 truncate">{i.instrument_code}</p>
                      <p className="text-[10px] text-gray-400 truncate">{toTitleCase(i.segment)}</p>
                    </div>
                    <span className="flex-shrink-0 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">Instrument</span>
                  </button>
                ))}
              </div>
            )}
            {suggest.reagents.length > 0 && (
              <div className="p-2">
                {suggest.reagents.map(p => (
                  <button key={p.product_code} onMouseDown={e => e.preventDefault()}
                    onClick={() => { navigate(`/app/products/${p.product_code}`); setShowSuggest(false); }}
                    className="w-full flex items-center justify-between gap-2 px-2 py-2 hover:bg-[#E0F4F5] rounded-md text-xs"
                    data-testid={`suggest-reagent-${p.product_code}`}
                  >
                    <div className="min-w-0 flex-1 text-left">
                      <p className="font-semibold text-gray-800 truncate">{toTitleCase(p.name)}</p>
                      <p className="text-[10px] text-gray-400 truncate">{p.product_code}{p.pack_size ? ` · ${p.pack_size}` : ""}</p>
                    </div>
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
                      <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Reagent</span>
                      {p.customer_price > 0 && <span className="text-[10px] font-bold text-[#008A99]">{Math.round(p.customer_price).toLocaleString()} pts</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tier Tracker — admin-configured card design (Point 16) or the default metallic gradient */}
      <div
        className={`relative rounded-2xl overflow-hidden shadow-lg ${customImg ? "" : `bg-gradient-to-br ${TIER_BG[tierName] || TIER_BG[tier] || TIER_BG.Silver}`}`}
        style={customImg ? { backgroundImage: `url(${customImg})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
        data-testid="tier-tracker"
      >
        {customImg && <div aria-hidden="true" className="absolute inset-0 bg-black/35" />}
        {/* Decorative blurred orbs (same recipe as Digital Card) */}
        <div aria-hidden="true" className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-10 -mt-10" />
        <div aria-hidden="true" className="absolute bottom-0 left-0 w-20 h-20 bg-white/10 rounded-full -ml-6 -mb-6" />
        {/* gentle top sheen */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0) 55%)" }} />
        <button
          onClick={() => navigate("/app/points")}
          className="relative w-full p-4 text-left active:brightness-95 transition"
          data-testid="tier-card-btn"
        >
          <div className="flex items-center gap-3">
            {/* Trophy chip */}
            <div
              className="relative w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 bg-white/20 backdrop-blur-sm ring-1 ring-white/30"
              data-testid="tier-metallic-badge"
            >
              <TierIcon className={`w-7 h-7 relative z-10 ${tierText} ${tierShadow}`} style={textStyle} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1.5">
                <span
                  className={`text-base font-black uppercase tracking-wide ${tierText} ${tierShadow}`}
                  style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif', ...(textStyle || {}) }}
                  data-testid="tier-name"
                >
                  {tierName}
                </span>
                <span className={`text-lg font-black ${tierText} ${tierShadow}`} style={textStyle}>
                  {balPts.toLocaleString()} <span className={`text-xs font-semibold ${tierTextDim}`} style={textStyle}>pts</span>
                </span>
              </div>
              <div className="relative h-2 bg-black/15 rounded-full overflow-hidden">
                <div
                  className={`absolute left-0 top-0 h-full rounded-full transition-all duration-700 ${customAccent ? "" : "bg-white/85"}`}
                  style={{ width: `${progressPercent}%`, ...(customAccent ? { background: customAccent } : {}) }}
                />
              </div>
              {nextTier && (
                <div className="flex justify-end mt-1">
                  <span className={`text-[10px] font-bold ${tierText}`}>
                    {pointsToNext.toLocaleString()} pts to {nextTier}
                  </span>
                </div>
              )}
              {!nextTier && (
                <div className="flex justify-end mt-1">
                  <span className={`text-[10px] font-bold ${tierText}`}>Maximum tier reached!</span>
                </div>
              )}
            </div>
            <ChevronRight className={`w-4 h-4 flex-shrink-0 ${tierTextSoft}`} />
          </div>
        </button>
        {/* Redeem CTA — same surface, slightly tinted */}
        <div className="relative border-t border-white/20 bg-black/5 backdrop-blur-sm px-4 py-3 flex items-center justify-between">
          <div>
            <p className={`text-[10px] uppercase tracking-wider font-semibold ${tierTextDim}`}>Available to Redeem</p>
            <p className={`text-base font-bold ${tierText} ${tierShadow}`}>{balPts.toLocaleString()} points</p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); navigate("/app/redeem"); }}
            className="flex items-center gap-1.5 bg-[#008A99] text-white text-xs font-semibold px-4 py-2 rounded-lg shadow-md hover:bg-[#004A52] transition-colors"
            data-testid="tier-redeem-btn"
          >
            <Gift className="w-4 h-4" /> Redeem
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-3" data-testid="quick-actions">
        {quickActions.map(a => (
          <button
            key={a.label}
            onClick={() => navigate(a.path)}
            className={`${a.color} ${a.textColor} rounded-xl py-4 px-3 flex flex-col items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-95 shadow-sm`}
            data-testid={`action-${a.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <a.icon className="w-6 h-6" />
            <span className="text-xs font-semibold leading-tight text-center">{a.label}</span>
          </button>
        ))}
      </div>

      {/* Banner Carousel — tapping a banner opens its link in-app (Point 21) */}
      {banners.length > 0 && (
        <div className="relative rounded-2xl overflow-hidden h-40" data-testid="banner-carousel">
          {banners.map((b, i) => {
            const hasLink = !!(b.cta_link && b.cta_link.trim());
            return (
            <div
              key={i}
              onClick={() => i === activeBanner && openBanner(b)}
              className={`absolute inset-0 transition-all duration-500 ${hasLink ? "cursor-pointer" : ""} ${i === activeBanner ? "opacity-100 translate-x-0" : "opacity-0 translate-x-full pointer-events-none"}`}
              data-testid={`banner-slide-${i}`}
            >
              {b.image_url ? (
                <img src={b.image_url} alt={b.title} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              ) : null}
              <div className={`${b.image_url ? 'hidden' : 'flex'} absolute inset-0 bg-gradient-to-r from-[#008A99] to-[#008A99] p-5 flex-col justify-center`}>
                <h3 className="text-white font-bold text-lg">{b.title}</h3>
                {b.subtitle && <p className="text-white/70 text-sm mt-1">{b.subtitle}</p>}
              </div>
              {hasLink && b.cta_text && (
                <span className="absolute bottom-3 right-3 z-10 text-[11px] font-semibold bg-white/90 text-[#008A99] px-3 py-1 rounded-full shadow" data-testid={`banner-cta-${i}`}>{b.cta_text}</span>
              )}
            </div>
          );})}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {banners.map((_, i) => (<div key={i} className={`w-1.5 h-1.5 rounded-full transition-all ${i === activeBanner ? "bg-white w-4" : "bg-white/40"}`} />))}
          </div>
        </div>
      )}

      {/* New Launch — horizontal scroll of admin-curated featured products */}
      <div data-testid="newly-launched-products">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-gray-900">New Launch</h3>
          <button onClick={() => navigate("/app/products")} className="text-xs text-[#008A99] font-semibold flex items-center gap-1" data-testid="view-all-products">
            View All <ChevronRight className="w-3 h-3" />
          </button>
        </div>

        {/* Horizontal scroll of hero cards (admin assets first, fallback to featured products) */}
        {(() => {
          const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
          // Build hero list: prefer admin-uploaded slots (in order), fallback to featured products
          const heroes = [];
          for (let i = 1; i <= 6; i++) {
            const a = newLaunchAssets[`new_launch_${i}`];
            if (a?.image_url) {
              heroes.push({
                key: `asset-${a.key}`,
                name: a.name || `New Launch ${i}`,
                category: "Featured",
                image: a.image_url.startsWith("http") ? a.image_url : `${BACKEND}${a.image_url}`,
                product_code: null,
              });
            }
          }
          // Fill remaining with featured products
          const remaining = Math.max(0, 4 - heroes.length);
          featuredFallback.slice(0, remaining).forEach(p => {
            heroes.push({
              key: `prod-${p.product_code}-${p.instrument_code || ''}-${p.reagent_type || ''}`,
              name: p.name,
              category: p.category,
              image: p.image_url,
              product_code: p.product_code,
              mrp: p.mrp,
            });
          });
          if (heroes.length === 0) return null;
          return (
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x" data-testid="featured-products" style={{ scrollbarWidth: 'thin' }}>
              {heroes.map(h => (
                <button
                  key={h.key}
                  onClick={() => h.product_code ? navigate(`/app/products/${h.product_code}`) : navigate("/app/products")}
                  className="flex-shrink-0 w-[60%] sm:w-[44%] bg-white rounded-2xl border overflow-hidden text-left snap-start hover:border-[#008A99] hover:shadow-md transition-all group"
                  data-testid={`featured-product-${h.key}`}
                >
                  <div className="aspect-square bg-gradient-to-br from-[#E0F4F5] to-[#F0F9FF] relative">
                    {h.image ? (
                      <img src={h.image} alt={h.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-12 h-12 text-[#008A99]/30" />
                      </div>
                    )}
                    <span className="absolute top-2 left-2 text-[8px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#008A99] text-white">New</span>
                  </div>
                  <div className="p-2.5">
                    {/* Admin-provided names in Asset Manager are rendered as-is (preserves CAPS / brand casing) */}
                    <p className="text-xs font-bold text-gray-900 truncate">{h.product_code ? toTitleCase(h.name) : (h.name || "")}</p>
                    <p className="text-[10px] text-gray-400 truncate">{h.product_code ? toTitleCase(h.category || "") : (h.category || "")}</p>
                    {h.mrp > 0 && <p className="text-xs font-bold text-[#008A99] mt-1">MRP {h.mrp}</p>}
                  </div>
                </button>
              ))}
            </div>
          );
        })()}
      </div>

      {/* About Us — concise brand intro shown right below New Launch */}
      <section data-testid="dashboard-about-section" className="relative overflow-hidden rounded-2xl border border-[#008A99]/10 bg-gradient-to-br from-white to-[#E0F4F5]/40">
        {/* Decorative top accent */}
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#008A99] via-[#2AAEB4] to-[#008A99]" />
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#008A99]">About Us</span>
            <span className="flex-1 h-px bg-gradient-to-r from-[#008A99]/30 to-transparent" />
          </div>
          <h3 className="text-xl font-black text-[#0A2540] leading-tight mb-3" data-testid="about-heading">
            {about?.heading || "Erba Transasia"}
          </h3>
          <div className="space-y-3 text-[13px] leading-relaxed text-gray-700" data-testid="about-body">
            {(about?.body && about.body.length > 0 ? about.body : [
              "Established over four decades ago, Erba Transasia has grown into India's leading multinational diagnostics company, delivering innovative and affordable diagnostic solutions to healthcare providers across more than 150 countries.",
            ]).map((para, idx, arr) => (
              <p key={idx} className={idx === arr.length - 1 && arr.length > 1 ? "italic font-semibold text-[#0A2540] pt-1" : ""}>
                {para}
              </p>
            ))}
          </div>
          <button
            onClick={() => navigate("/app/about")}
            className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[#008A99] hover:text-[#004A52] transition-colors"
            data-testid="about-read-more"
          >
            Learn more <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </section>

      {/* LinkedIn — follow our page */}
      <a
        href="https://www.linkedin.com/company/transasia-bio-medicals-ltd/"
        target="_blank"
        rel="noopener noreferrer"
        className="group relative flex items-center gap-3 overflow-hidden rounded-2xl bg-gradient-to-br from-[#0A66C2] to-[#004182] p-4 shadow-md hover:shadow-lg active:scale-[0.98] transition-all"
        data-testid="linkedin-link"
      >
        {/* Subtle pattern overlay */}
        <div aria-hidden="true" className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
        <div aria-hidden="true" className="absolute -left-4 -bottom-8 w-24 h-24 rounded-full bg-white/5 blur-xl" />
        <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm ring-1 ring-white/25 flex-shrink-0">
          <Linkedin className="w-6 h-6 text-white" fill="currentColor" />
        </div>
        <div className="relative flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70">Follow us on</p>
          <p className="text-sm font-black text-white truncate">Erba Transasia · LinkedIn</p>
          <p className="text-[11px] font-medium text-white/80 truncate">Stay updated with launches, news &amp; events</p>
        </div>
        <ExternalLink className="relative w-4 h-4 text-white/80 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
      </a>

      {/* Training & News sections removed per spec — anything below New Launch deleted */}
    </div>
  );
}
